import React from 'react';
import { Edit, Trash2, Package } from 'lucide-react';

interface Category {
  _id?: string;
  id?: string;
  slug?: { tr: string; en: string; es: string };
  title: { tr: string; en: string; es: string };
  description: { tr: string; en: string; es: string };
  image?: string;
  order?: number;
  productCount?: number;
  createdAt?: string;
  updatedAt?: string;
}

interface CategoryListProps {
  categories: Category[];
  loading: boolean;
  onEdit: (category: Category) => void;
  onDelete: (category: Category) => void;
  onSelect: (category: Category) => void;
  onUpdateOrder?: (categoryId: string, order: number) => void;
  selectedCategory: Category | null;
}

const CategoryList: React.FC<CategoryListProps> = ({
  categories,
  loading,
  onEdit,
  onDelete,
  onSelect,
  onUpdateOrder,
  selectedCategory,
}) => {
  const [editingOrderId, setEditingOrderId] = React.useState<string | null>(null);
  const [orderValue, setOrderValue] = React.useState<number>(0);

  const handleOrderClick = (category: Category, e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingOrderId(category._id || category.id || null);
    setOrderValue(category.order ?? 0);
  };

  const handleOrderSave = async (categoryId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    console.log('CategoryList: Saving order', { categoryId, orderValue });
    if (onUpdateOrder) {
      await onUpdateOrder(categoryId, orderValue);
    }
    setEditingOrderId(null);
  };

  const handleOrderCancel = (e: React.MouseEvent) => {
    e.stopPropagation();
    setEditingOrderId(null);
  };
  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        <span className="ml-2 text-gray-600">Yükleniyor...</span>
      </div>
    );
  }

  if (categories.length === 0) {
    return (
      <div className="text-center py-12 text-gray-500">
        Henüz kategori bulunmuyor.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {categories.map((category) => {
        const isSelected = selectedCategory?._id === category._id || selectedCategory?.id === category.id;
        
        return (
          <div
            key={category._id || category.id}
            onClick={() => onSelect(category)}
            className={`relative bg-white border-2 rounded-lg p-4 cursor-pointer transition-all hover:shadow-md ${
              isSelected
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-200 hover:border-primary-300'
            }`}
          >
            {/* Category Image */}
            {category.image && (
              <div className="mb-3 rounded-lg overflow-hidden h-32 bg-gray-100">
                <img
                  src={category.image}
                  alt={category.title.tr || 'Category'}
                  className="w-full h-full object-cover"
                />
              </div>
            )}

            {/* Category Info */}
            <div className="mb-3">
              <h3 className="font-medium text-gray-900 mb-1">
                {category.title.tr || 'İsimsiz Kategori'}
              </h3>
              {category.description.tr && (
                <p className="text-sm text-gray-600 line-clamp-2">
                  {category.description.tr}
                </p>
              )}
            </div>

            {/* Product Count & Order */}
            <div className="flex items-center justify-between text-sm text-gray-500 mb-3">
              <div className="flex items-center">
                <Package className="h-4 w-4 mr-1" />
                <span>{category.productCount || 0} ürün</span>
              </div>
              
              {/* Order Display/Edit */}
              {editingOrderId === (category._id || category.id) ? (
                <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                  <input
                    type="number"
                    min="0"
                    value={orderValue}
                    onChange={(e) => setOrderValue(parseInt(e.target.value) || 0)}
                    className="w-16 px-2 py-1 text-xs border border-primary-300 rounded focus:ring-1 focus:ring-primary-500"
                    onClick={(e) => e.stopPropagation()}
                  />
                  <button
                    onClick={(e) => handleOrderSave(category._id || category.id || '', e)}
                    className="px-2 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700"
                  >
                    ✓
                  </button>
                  <button
                    onClick={handleOrderCancel}
                    className="px-2 py-1 text-xs bg-gray-400 text-white rounded hover:bg-gray-500"
                  >
                    ✕
                  </button>
                </div>
              ) : (
                <button
                  onClick={(e) => handleOrderClick(category, e)}
                  className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors"
                  title="Sıra numarasını değiştir"
                >
                  Sıra: {category.order ?? 0}
                </button>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(category);
                }}
                className="p-2 text-primary-600 hover:bg-primary-100 rounded-md transition-colors"
                title="Düzenle"
              >
                <Edit className="h-4 w-4" />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(category);
                }}
                className="p-2 text-red-600 hover:bg-red-100 rounded-md transition-colors"
                title="Sil"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            {/* Selected Indicator */}
            {isSelected && (
              <div className="absolute top-2 right-2">
                <div className="h-3 w-3 bg-primary-600 rounded-full"></div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default CategoryList;

